import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/pages/admin/AdminShell";
import { toast } from "sonner";
import {
  Search,
  ShieldOff,
  ShieldCheck,
  Trash2,
  ChevronRight,
} from "lucide-react";

export default function AdminUsers() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [suspended, setSuspended] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&role=${role}&suspended=${suspended}&limit=200`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role, suspended]);

  useEffect(() => {
    const t = setTimeout(() => { if (user && user.role === "admin") load(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const toggleSuspend = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_suspended: !u.is_suspended });
      toast.success(u.is_suspended ? "Account unsuspended." : "Account suspended.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This removes their wallet, transactions and cards.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Account deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminShell>
      <div className="text-eyebrow text-[#4F4D45]">Compliance</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">All users</h1>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4F4D45]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or wallet ID…" data-testid="admin-users-search"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm focus:border-[#1B3526] focus:outline-none" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role"
                className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm">
          <option value="all">All roles</option>
          <option value="user">Users</option>
          <option value="admin">Admins</option>
        </select>
        <select value={suspended} onChange={(e) => setSuspended(e.target.value)} data-testid="admin-users-suspended"
                className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm">
          <option value="all">All statuses</option>
          <option value="no">Active</option>
          <option value="yes">Suspended</option>
        </select>
      </div>

      <div className="mt-6 fv-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#4F4D45]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#4F4D45]" data-testid="admin-users-empty">No users match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[#F0ECE0] text-left text-xs uppercase tracking-wider text-[#4F4D45]">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Wallet</th>
                <th className="px-5 py-3">Balance (live/demo)</th>
                <th className="px-5 py-3">KYC</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D2CDBE]">
              {items.map((u) => (
                <tr key={u.id} className="hover:bg-[#F0ECE0]/60" data-testid={`admin-users-row-${u.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/admin/users/${u.id}`} className="text-[#1B3526] hover:underline font-medium flex items-center gap-1.5">
                      {u.name} <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                    <div className="text-xs text-[#4F4D45]">{u.email}</div>
                  </td>
                  <td className="px-5 py-3 capitalize">{u.account_type}{u.role === "admin" && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#A85943]">admin</span>}</td>
                  <td className="px-5 py-3 capitalize">{u.banking_mode}</td>
                  <td className="px-5 py-3 font-mono text-xs">{u.wallet_id}</td>
                  <td className="px-5 py-3 font-mono">${u.balance_live.toFixed(2)} / ${u.balance_demo.toFixed(2)}</td>
                  <td className="px-5 py-3 capitalize">{u.kyc_status || "—"}</td>
                  <td className="px-5 py-3">{u.is_suspended ? <span className="text-[#992F2F]">Suspended</span> : <span className="text-[#264C39]">Active</span>}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => toggleSuspend(u)} title={u.is_suspended ? "Unsuspend" : "Suspend"} data-testid={`admin-users-suspend-${u.id}`} className="p-1.5 text-[#4F4D45] hover:text-[#A85943] rounded">
                      {u.is_suspended ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => remove(u)} title="Delete" data-testid={`admin-users-delete-${u.id}`} className="p-1.5 text-[#4F4D45] hover:text-[#992F2F] rounded ml-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
